import type { TrackColor } from '@/types/tour'

/**
 * Service for managing tour colors in the application
 */
export class TrackColorService {
  // Available predefined colors for tours
  private static readonly colors: TrackColor[] = [
    { value: '#3388ff', label: 'Blue' },
    { value: '#33cc33', label: 'Green' },
    { value: '#ff3333', label: 'Red' },
    { value: '#ff9900', label: 'Orange' },
    { value: '#9900cc', label: 'Purple' },
    { value: '#00cccc', label: 'Teal' },
    { value: '#ff6600', label: 'Dark Orange' },
    { value: '#0099cc', label: 'Sky Blue' },
    { value: '#993399', label: 'Violet' },
    { value: '#5BD75B', label: 'Light Green' },
    { value: '#FF5252', label: 'Bright Red' },
    { value: '#FFD54F', label: 'Yellow' },
    { value: '#000000', label: 'Black' },
  ]

  // Default color to use when none is specified
  private static readonly defaultColor = TrackColorService.colors[0].value

  /**
   * Get all available tour colors
   */
  public static getColors(): TrackColor[] {
    return [...this.colors]
  }

  /**
   * Get the default tour color
   */
  public static getDefaultColor(): string {
    return this.defaultColor
  }

  /**
   * Check if a given color value is one of the predefined colors
   */
  public static isValidColor(color?: string): boolean {
    if (!color) return false
    return this.colors.some((c) => c.value === color)
  }

  /**
   * Get the label for a specific color value
   */
  public static getColorLabel(colorValue: string): string | undefined {
    return this.colors.find((c) => c.value === colorValue)?.label
  }

  /**
   * Ensures a tour color is valid, returning the default color if the provided color is invalid
   */
  public static ensureValidColor(color?: string): string {
    if (color && this.isValidColor(color)) {
      return color
    }
    return this.defaultColor
  }

  /**
   * Get the color for a tour, ensuring it's valid or returning the default
   */
  public static getTourColor(tourId: string, tourColor?: string): string {
    return this.ensureValidColor(tourColor)
  }

  /**
   * Get the color for a tour based on its index
   * This ensures consistent color rotation across the application
   */
  public static getColorByIndex(index: number): string {
    return this.colors[Math.abs(index) % this.colors.length].value
  }

  /**
   * Pick a random color from the predefined palette.
   */
  public static getRandomColor(): string {
    return this.colors[Math.floor(Math.random() * this.colors.length)].value
  }

  /**
   * Get the next palette color after the provided color.
   * Falls back to the default color if the provided value is not part of the palette.
   */
  public static getNextColor(color?: string): string {
    const colorIndex = color ? this.colors.findIndex((candidate) => candidate.value === color) : -1
    if (colorIndex < 0) return this.defaultColor
    return this.getColorByIndex(colorIndex + 1)
  }

  /**
   * Choose the color for a newly created track within a specific tour.
   * The first track gets a random palette color; subsequent tracks cycle from the last valid color.
   */
  public static getNewTrackColorForTour(existingTracks: ReadonlyArray<{ color?: string }>): string {
    if (existingTracks.length === 0) {
      return this.getRandomColor()
    }

    for (let index = existingTracks.length - 1; index >= 0; index -= 1) {
      const color = existingTracks[index].color
      if (this.isValidColor(color)) {
        return this.getNextColor(color)
      }
    }

    return this.getColorByIndex(existingTracks.length)
  }

  /**
   * Calculate if white or black text should be used on top of a given background color
   * Returns the appropriate text color based on background luminance
   */
  public static getTextColor(bgColor: string): string {
    // Simple luminance calculation
    const r = parseInt(bgColor.slice(1, 3), 16)
    const g = parseInt(bgColor.slice(3, 5), 16)
    const b = parseInt(bgColor.slice(5, 7), 16)

    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

    // Use white text on dark backgrounds, black text on light backgrounds
    return luminance > 0.5 ? '#000000' : '#FFFFFF'
  }
}
